import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import toast from "react-hot-toast";
import DatePicker from "../Common/DatePicker";
import { employeeConfirmationService } from "../../services/api";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
}

function isBeforeDate(a, b) {
  if (!a || !b) return false;
  return new Date(a) < new Date(b);
}

export default function EmployeeConfirmationModal({
  darkMode = false,
  employee,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [context, setContext] = useState(null);
  const [decision, setDecision] = useState("");
  const [confirmationDate, setConfirmationDate] = useState("");
  const [extendProbation, setExtendProbation] = useState(false);
  const [extendedProbationDate, setExtendedProbationDate] = useState("");
  const [extensionReason, setExtensionReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [earlyOverride, setEarlyOverride] = useState(false);

  const policy = context?.policy;
  const runtime = context?.runtime;
  const expectedDate = runtime?.expectedConfirmationDate || context?.dates?.expectedConfirmationDate;
  const policyEffectiveDate = policy?.effectiveDate || context?.dates?.policyEffectiveDate;
  const probationStart = runtime?.probationStartDate || context?.dates?.probationStartDate;
  const joiningDate = employee?.joiningDate || employee?.dateOfJoin || employee?.joinDate;

  const isEarlyDate = useMemo(
    () => decision === "yes" && confirmationDate && expectedDate && isBeforeDate(confirmationDate, expectedDate),
    [decision, confirmationDate, expectedDate]
  );

  const loadData = useCallback(async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const ctxRes = await employeeConfirmationService.getContext(employee.id);
      setContext(ctxRes?.data || null);
      const exp =
        ctxRes?.data?.runtime?.expectedConfirmationDate ||
        ctxRes?.data?.dates?.expectedConfirmationDate;
      if (exp) setConfirmationDate(exp);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load confirmation policy context");
    } finally {
      setLoading(false);
    }
  }, [employee?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const workflowPending = runtime?.status === "pending_approval";
  const canExtend = policy?.extensionAllowed !== "no";

  const handleSave = async () => {
    if (!decision) {
      toast.error("Please select Yes or No");
      return;
    }

    if (!runtime?.id) {
      toast.error("No confirmation runtime record. Employee must be on Probation.");
      return;
    }

    if (decision === "yes") {
      if (!confirmationDate) {
        toast.error("Please select confirmation date");
        return;
      }
      if (joiningDate && isBeforeDate(confirmationDate, joiningDate)) {
        toast.error("Confirmation date cannot be before joining date");
        return;
      }
      if (isEarlyDate && !earlyOverride) {
        toast.error("Acknowledge early confirmation override before saving");
        return;
      }
      if (isEarlyDate && !remarks.trim()) {
        toast.error("Remarks are required for early confirmation override");
        return;
      }

      setActing(true);
      try {
        if (workflowPending && !isEarlyDate) {
          if (!remarks.trim()) {
            toast.error("Remarks are required to approve workflow step");
            setActing(false);
            return;
          }
          const res = await employeeConfirmationService.approve(runtime.id, {
            remarks,
            comments: remarks,
            confirmationDate,
          });
          toast.success(res?.completed ? "Employee confirmed" : "Approval step recorded");
        } else {
          const res = await employeeConfirmationService.confirm(runtime.id, {
            confirmationDate,
            remarks,
            comments: remarks,
            earlyConfirmationOverride: isEarlyDate && earlyOverride,
          });
          if (res?.workflowStarted) {
            toast.success(
              res.message ||
                "Confirmation workflow started. Checklist tasks are in Action Insights."
            );
          } else {
            toast.success("Employee confirmed as per policy");
          }
        }
        onSaved?.();
        onClose();
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to confirm employee");
      } finally {
        setActing(false);
      }
      return;
    }

    if (decision === "no") {
      if (extendProbation) {
        if (!extendedProbationDate) {
          toast.error("Please select extended probation end date");
          return;
        }
        if (!extensionReason.trim()) {
          toast.error("Extension reason is required");
          return;
        }
        if (!canExtend) {
          toast.error("Policy does not allow probation extension");
          return;
        }
        setActing(true);
        try {
          await employeeConfirmationService.extendProbation(runtime.id, {
            newEndDate: extendedProbationDate,
            extendedProbationDate,
            reason: extensionReason,
            remarks: extensionReason,
          });
          toast.success("Probation extended as per policy");
          onSaved?.();
          onClose();
        } catch (error) {
          toast.error(error?.response?.data?.message || "Failed to extend probation");
        } finally {
          setActing(false);
        }
      } else {
        toast.success("Confirmation deferred — employee remains on probation");
        onClose();
      }
    }
  };

  const panelClass = darkMode
    ? "bg-[#2C2C2C] text-[#E0E0E0]"
    : "bg-white text-gray-900";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div
        className={`rounded-md shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${panelClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex justify-between items-center p-6 border-b border-gray-200/20 bg-inherit">
          <h2 className="text-lg font-semibold">Employee Confirmation</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="text-center">
            <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
              Employee:{" "}
              <span className="font-semibold">
                {employee?.user?.name || employee?.name} ({employee?.empCode})
              </span>
            </p>
            {joiningDate && (
              <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                Date of Join: {formatDate(joiningDate)}
              </p>
            )}
          </div>

          {loading ? (
            <p className="text-sm text-center py-8">Loading policy context…</p>
          ) : !context?.eligible ? (
            <div
              className={`p-4 rounded border text-sm ${
                darkMode ? "border-amber-700 bg-amber-900/20" : "border-amber-300 bg-amber-50"
              }`}
            >
              {context?.reason || "Employee is not eligible for confirmation tracking."}
            </div>
          ) : (
            <>
              <div
                className={`p-4 rounded border text-sm space-y-2 ${
                  darkMode ? "border-gray-600 bg-gray-800/50" : "border-blue-200 bg-blue-50"
                }`}
              >
                <h3 className="font-semibold text-sm">Applicable Confirmation Policy</h3>
                {policy ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div>
                        <span className="opacity-70">Policy: </span>
                        <span className="font-medium">{policy.policyName}</span>
                      </div>
                      <div>
                        <span className="opacity-70">Effective from: </span>
                        <span className="font-medium">{formatDate(policyEffectiveDate)}</span>
                      </div>
                      <div>
                        <span className="opacity-70">Probation period: </span>
                        <span className="font-medium">{policy.probationDays} days</span>
                      </div>
                      <div>
                        <span className="opacity-70">Probation starts: </span>
                        <span className="font-medium">{formatDate(probationStart)}</span>
                      </div>
                      <div>
                        <span className="opacity-70">Policy confirmation date: </span>
                        <span className="font-semibold text-blue-700 dark:text-blue-300">
                          {formatDate(expectedDate)}
                        </span>
                      </div>
                      <div>
                        <span className="opacity-70">Approval mode: </span>
                        <span className="font-medium">
                          {policy.approvalMethod === "workflow"
                            ? `Workflow (${policy.workflowStepCount} steps)`
                            : "Auto approved"}
                        </span>
                      </div>
                    </div>
                    {policyEffectiveDate &&
                      joiningDate &&
                      new Date(policyEffectiveDate) > new Date(joiningDate) && (
                        <p className={`text-xs mt-2 ${darkMode ? "text-amber-300" : "text-amber-800"}`}>
                          Policy became effective ({formatDate(policyEffectiveDate)}) after date of join (
                          {formatDate(joiningDate)}). Probation still starts from joining date; confirmation
                          date is joining date + {policy.probationDays} day(s).
                        </p>
                      )}
                    <p className={`text-xs mt-2 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                      Saving Yes starts the policy workflow. Checklist tasks are assigned to the configured
                      roles in Employee Action Insights.
                    </p>
                  </>
                ) : (
                  <p className="text-amber-700">
                    No matching active policy today. Default probation rules apply.
                  </p>
                )}
                {runtime && (
                  <div className="pt-2 border-t border-gray-300/30">
                    <span className="opacity-70">Runtime status: </span>
                    <span className="font-medium capitalize">
                      {runtime.status?.replace(/_/g, " ")}
                    </span>
                    {workflowPending && runtime.pendingLabel && (
                      <span className="ml-2">
                        — Pending with: <strong>{runtime.pendingLabel}</strong>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm font-medium mb-3">Is the employee confirmed as permanent?</p>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="confirmation"
                      value="yes"
                      checked={decision === "yes"}
                      onChange={() => setDecision("yes")}
                      className="mr-3"
                    />
                    <span className="text-sm">Yes — confirm (policy workflow applies)</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="confirmation"
                      value="no"
                      checked={decision === "no"}
                      onChange={() => setDecision("no")}
                      className="mr-3"
                    />
                    <span className="text-sm">No — defer / extend probation</span>
                  </label>
                </div>
              </div>

              {decision === "yes" && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Date of Confirmation <span className="text-red-500">*</span>
                    {expectedDate && (
                      <span className="font-normal text-xs ml-2 opacity-70">
                        (Policy date: {formatDate(expectedDate)})
                      </span>
                    )}
                  </label>
                  <DatePicker
                    value={confirmationDate || expectedDate || ""}
                    min={joiningDate}
                    onChange={(e) => setConfirmationDate(e.target.value)}
                    className="w-full"
                    validateOnType
                  />
                  {isEarlyDate && (
                    <div
                      className={`mt-3 p-3 rounded border flex gap-2 text-sm ${
                        darkMode ? "border-amber-600 bg-amber-900/30" : "border-amber-400 bg-amber-50"
                      }`}
                    >
                      <AlertTriangle className="shrink-0 text-amber-600" size={18} />
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-200">
                          Early confirmation warning
                        </p>
                        <p className="text-xs mt-1 text-amber-700 dark:text-amber-300">
                          Selected date ({formatDate(confirmationDate)}) is before the policy-driven
                          confirmation date ({formatDate(expectedDate)}). Only an admin may override this
                          with remarks.
                        </p>
                        <label className="flex items-center mt-2 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={earlyOverride}
                            onChange={(e) => setEarlyOverride(e.target.checked)}
                            className="mr-2"
                          />
                          I acknowledge early confirmation override (admin)
                        </label>
                      </div>
                    </div>
                  )}
                  <label className="block text-sm font-medium mt-3 mb-1">
                    Remarks {workflowPending || isEarlyDate ? <span className="text-red-500">*</span> : null}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={2}
                    className={`w-full p-2 border rounded text-sm ${
                      darkMode ? "bg-gray-800 border-gray-600" : "border-gray-300"
                    }`}
                    placeholder="Approval remarks or early confirmation reason"
                  />
                </div>
              )}

              {decision === "no" && (
                <div>
                  {canExtend ? (
                    <>
                      <label className="flex items-center cursor-pointer mb-3">
                        <input
                          type="checkbox"
                          checked={extendProbation}
                          onChange={(e) => setExtendProbation(e.target.checked)}
                          className="mr-3"
                        />
                        <span className="text-sm">
                          Extend probation
                          {policy?.maxExtensions != null && (
                            <span className="text-xs opacity-70 ml-1">
                              (max {policy.maxExtensions}, used {runtime?.extensionCount || 0})
                            </span>
                          )}
                        </span>
                      </label>
                      {extendProbation && (
                        <>
                          <label className="block text-sm font-medium mb-2">
                            Extended Probation End Date <span className="text-red-500">*</span>
                          </label>
                          <DatePicker
                            value={extendedProbationDate}
                            min={expectedDate || joiningDate}
                            onChange={(e) => setExtendedProbationDate(e.target.value)}
                            className="w-full mb-3"
                            validateOnType
                          />
                          <label className="block text-sm font-medium mb-2">
                            Extension Reason <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={extensionReason}
                            onChange={(e) => setExtensionReason(e.target.value)}
                            rows={2}
                            className={`w-full p-2 border rounded text-sm ${
                              darkMode ? "bg-gray-800 border-gray-600" : "border-gray-300"
                            }`}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-amber-700">Policy does not allow probation extension.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 p-6 border-t border-gray-200/20 bg-inherit">
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              darkMode
                ? "bg-gray-600 text-white hover:bg-gray-700"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={acting || loading || !context?.eligible}
            onClick={handleSave}
            className="px-4 py-2 rounded-md text-sm font-medium rh-btn-primary disabled:opacity-50"
          >
            {acting ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
